import { loginWithGoogle, auth } from '@/lib/firebase';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Leggi il redirect desiderato dalla querystring, default /
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirectTo') || '/';

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) {
        // Appena autenticato, vai alla pagina desiderata
        navigate(redirectTo, { replace: true });
      }
    });
    return () => unsub();
  }, [navigate, redirectTo]);

  const handleLogin = async () => {
    try {
      await loginWithGoogle();
    } catch (e) {
      console.error('Errore login Google:', e);
      alert('Impossibile completare il login. Controlla la configurazione Firebase e riprova.');
    }
  };

  return (
    <div className="grid min-h-[100dvh] bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-white lg:grid-cols-2">
      {/* Left Panel - Brand/Image */}
      <div className="relative hidden flex-col bg-zinc-100 p-10 dark:bg-zinc-900 lg:flex">
        {/* Logo */}
        <div className="flex items-center gap-2 text-zinc-900 dark:text-white">
          <img 
            src="/logo_simbolo.png" 
            alt="Polo9 Logo" 
            className="w-8 h-8"
          />
          <span className="text-lg font-semibold">Polo9</span>
        </div>

        {/* Testimonial/Quote at bottom */}
        <div className="mt-auto">
          <blockquote className="space-y-2">
            <p className="text-lg text-zinc-700 dark:text-zinc-300">
              "La forza dei dati, al servizio della nostra cooperazione."
            </p>
            <footer className="text-sm text-zinc-500 dark:text-zinc-400">
              — Polo9
            </footer>
          </blockquote>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex flex-col bg-white dark:bg-zinc-950">
        {/* Mobile Logo (visible only on mobile) */}
        <div className="flex items-center gap-2 p-6 text-zinc-900 dark:text-white lg:hidden">
          <img 
            src="/logo_simbolo.png" 
            alt="Polo9 Logo" 
            className="w-8 h-8"
          />
          <span className="text-lg font-semibold">Polo9</span>
        </div>

        {/* Centered Form */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
          <div className="w-full max-w-[350px] space-y-6">
            {/* Header */}
            <div className="flex flex-col space-y-2 text-center">
              <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-white">
                Accedi al tuo account
              </h1>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Usa il tuo account Google @polo9.org per accedere
              </p>
            </div>

            {/* Login Button */}
            <button
              onClick={handleLogin}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 py-3 text-sm font-medium text-white shadow-sm transition-all duration-150 hover:scale-[1.02] hover:bg-zinc-800 active:scale-[0.98] active:bg-zinc-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-700 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-200 dark:active:bg-zinc-300 dark:focus-visible:outline-zinc-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-5 h-5">
                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.155,7.961,3.039l5.657-5.657C33.109,6.053,28.805,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,16.108,18.961,14,24,14c3.059,0,5.842,1.155,7.961,3.039l5.657-5.657C33.109,6.053,28.805,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.563,5.057C9.469,39.556,16.227,44,24,44z"/>
                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.793,2.237-2.231,4.166-3.98,5.571c0.001-0.001,0.001-0.001,0.002-0.002l6.19,5.238C36.996,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
              </svg>
              Continua con Google
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-zinc-200 dark:border-zinc-800" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-zinc-500 dark:bg-zinc-950 dark:text-zinc-400">
                  Accesso riservato
                </span>
              </div>
            </div>

            {/* Info */}
            <p className="text-center text-xs text-zinc-600 dark:text-zinc-400">
              Accesso limitato agli utenti del dominio{' '}
              <span className="font-medium text-zinc-700 dark:text-zinc-300">@polo9.org</span>
            </p>

            {/* Terms */}
            <p className="px-8 text-center text-xs text-zinc-600 dark:text-zinc-400">
              Accedendo, accetti i nostri{' '}
              <a href="#" className="underline underline-offset-4 hover:text-zinc-900 dark:hover:text-zinc-200">
                Termini di Servizio
              </a>{' '}
              e la{' '}
              <a href="#" className="underline underline-offset-4 hover:text-zinc-900 dark:hover:text-zinc-200">
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
